import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { CircularProgress, Box } from "@mui/material";

// Eager — user hits these on every session
import LoggedInLayout from "../layout";
import Landing from "../pages/Landing/";
import Dashboard from "../pages/Dashboard/";
import Tickets from "../pages/Tickets/";
import Signup from "../pages/Signup/";
import Login from "../pages/Login/";
import Contacts from "../pages/Contacts/";
import TrialExpired from "../pages/TrialExpired/";

// Lazy — lower-traffic pages; deferred to their own chunks
const Settings        = lazy(() => import("../pages/Settings/"));
const Reports         = lazy(() => import("../pages/Reports/"));
const SuperAdmin      = lazy(() => import("../pages/SuperAdmin/"));
const Connections     = lazy(() => import("../pages/Connections/"));
const Users           = lazy(() => import("../pages/Users/"));
const QuickAnswers    = lazy(() => import("../pages/QuickAnswers/"));
const Queues          = lazy(() => import("../pages/Queues/"));
const Billing         = lazy(() => import("../pages/Billing/"));
const GeneralSettings = lazy(() => import("../pages/GeneralSettings/"));
const TicketSettings  = lazy(() => import("../pages/TicketSettings/"));
const Notifications   = lazy(() => import("../pages/Notifications/"));
const ApiIntegrations = lazy(() => import("../pages/ApiIntegrations/"));

import { AuthProvider } from "../context/Auth/AuthContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import { ThemeProvider } from "../context/DarkMode";
import PrivateRoute, { PublicRoute } from "./Route";

const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
    <CircularProgress />
  </Box>
);

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
            <Route path="/trial-expired" element={<PrivateRoute skipTrialCheck><TrialExpired /></PrivateRoute>} />
            <Route
              path="/*"
              element={
                <PrivateRoute>
                  <WhatsAppsProvider>
                    <LoggedInLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/tickets/:ticketId?" element={<Tickets />} />
                          <Route path="/contacts" element={<Contacts />} />
                          <Route path="/connections" element={<Connections />} />
                          <Route path="/users" element={<Users />} />
                          <Route path="/quickAnswers" element={<QuickAnswers />} />
                          <Route path="/Queues" element={<Queues />} />
                          <Route path="/billing" element={<Billing />} />
                          <Route path="/general" element={<GeneralSettings />} />
                          <Route path="/ticket-settings" element={<TicketSettings />} />
                          <Route path="/notifications" element={<Notifications />} />
                          <Route path="/api" element={<ApiIntegrations />} />
                          <Route path="/Settings" element={<Settings />} />
                          <Route path="/reports" element={<Reports />} />
                          <Route path="/superadmin" element={<PrivateRoute requiredRole="superadmin"><SuperAdmin /></PrivateRoute>} />
                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </Suspense>
                    </LoggedInLayout>
                  </WhatsAppsProvider>
                </PrivateRoute>
              }
            />
          </Routes>
          <Toaster toastOptions={{ duration: 3000 }} />
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default AppRoutes;
